import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, Building2, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHero, SubNav, SectionHeading, Reveal, CTA, Eyebrow } from "../components/Shared";
import { IMAGES, COST_SAVINGS_PROGRAMS, LEADERSHIP, PRESIDENT_MESSAGE, NAV } from "../mock";

const useSubNav = () => {
  const { pathname } = useLocation();
  return NAV[0].children.map((c) => ({ ...c, active: pathname === c.path }));
};

export const WhoWeAreOverview = () => (
  <>
    <PageHero
      eyebrow="Who we are"
      title="Nonprofit shared services from New England's most trusted management firm."
      intro="Great North Advantage is the nonprofit services division of Great North Property Management. We give you attention from the senior management of the company, unsurpassed service, and knowledge of the marketplace."
      image={IMAGES.collaboration}
    />
    <SubNav items={useSubNav()} />
    <section className="section" data-testid="who-we-are-content">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <div className="prose-gn lg:col-span-7">
          <Reveal>
            <p className="lead">We handle your nonprofit's financial, administrative, contractual and legal matters so your staff and board can focus on the mission.</p>
            <p>Great North is the most trusted management firm in New England, and Great North Advantage brings that depth to the nonprofit sector. Our parent company's portfolio is comprised of over 350 associations and cooperatives. Great North is financially stable and has no debt. Our corporate headquarters is located in Exeter, New Hampshire. We also have offices in Merrimack, Bretton Woods and Lebanon, New Hampshire, as well as offices in Peabody, Norwood and Worcester, Massachusetts, Wells, Maine and Warwick, Rhode Island.</p>
            <p>As one of the largest management firms in New England, Great North is able to negotiate better pricing for our clients given our overall buying power in the market and the amount of business that we award annually. Great North Advantage is a one-stop portal for handling all of your nonprofit's administrative and management needs.</p>
            <p>In over 36 years of business, we have coordinated a large number of cost savings programs for our clients. These programs have resulted in significant cost savings and improved efficiency by helping to eliminate non value added steps and processes which positively improved many non profit organizational budgets.</p>
            <p>We hope to work with you and look forward to delivering our expertise and capacity in these areas to you.</p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={100} className="rounded-3xl border border-brand-100 bg-white p-8">
            <Eyebrow>Cost savings programs</Eyebrow>
            <h3 className="mt-3 font-serif text-2xl text-ink-900">Programs we've coordinated for nonprofit clients</h3>
            <ul className="mt-6 space-y-3">
              {COST_SAVINGS_PROGRAMS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[15px] text-ink-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700"><Check size={12} /></span>
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={180} className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-brand-700 p-6 text-white">
              <Building2 size={22} className="text-gold-200" />
              <p className="mt-4 font-serif text-3xl">350+</p>
              <p className="text-sm text-sand-200">Organizations &amp; communities served</p>
            </div>
            <div className="rounded-2xl bg-sand-100 p-6">
              <MapPin size={22} className="text-brand-700" />
              <p className="mt-4 font-serif text-3xl text-ink-900">9</p>
              <p className="text-sm text-ink-600">Offices in four states</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
    <section className="section bg-white">
      <div className="container-x">
        <SectionHeading eyebrow="Leadership" title="The people behind the promise." />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {LEADERSHIP.map((p, i) => (
            <Reveal key={p.name} delay={i * 80}>
              <Link to="/who-we-are/leadership" className="card-lift group flex items-center gap-5 rounded-3xl border border-brand-100 bg-sand-50 p-5">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full">
                  <img src={p.photo} alt={p.name} className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div>
                  <h3 className="font-serif text-lg leading-tight text-ink-900">{p.name}</h3>
                  <p className="mt-1 text-sm text-ink-600">{p.title}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Button asChild className="btn-pill mt-10 bg-brand-600 text-white hover:bg-brand-700">
          <Link to="/who-we-are/leadership">Full leadership bios <ArrowRight size={15} className="ml-1.5" /></Link>
        </Button>
      </div>
    </section>
    <CTA />
  </>
);

export const Leadership = () => (
  <>
    <PageHero compact eyebrow="Who we are" title="Leadership." intro="Senior management that stays personally involved with every client we serve." image={IMAGES.building} />
    <SubNav items={useSubNav()} />
    <section className="section" data-testid="leadership-content">
      <div className="container-x space-y-10">
        {LEADERSHIP.map((p, i) => (
          <Reveal key={p.name} className="grid gap-6 rounded-3xl border border-brand-100 bg-white p-6 md:grid-cols-12 lg:p-8" data-testid={`leader-${i}`}>
            <div className="md:col-span-2">
              <div className="aspect-square w-32 overflow-hidden rounded-2xl md:w-full md:max-w-[150px]">
                <img src={p.photo} alt={p.name} className="h-full w-full object-cover object-top" />
              </div>
            </div>
            <div className="md:col-span-10">
              <span className="font-mono text-xs tracking-widest text-brand-600">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="mt-2 font-serif text-3xl text-ink-900">{p.name}</h2>
              <p className="mt-1 text-brand-700 font-medium">{p.title}</p>
              <p className="mt-5 text-[15px] leading-relaxed text-ink-600 lg:text-base">{p.bio}</p>
              {p.link && (
                <Link to={p.link.path} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900">
                  {p.link.label} <ArrowRight size={15} />
                </Link>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <CTA />
  </>
);

export const PresidentsMessage = () => (
  <>
    <PageHero compact eyebrow="Who we are" title="President's Message." image={IMAGES.team} />
    <SubNav items={useSubNav()} />
    <section className="section" data-testid="presidents-message-content">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="sticky top-32 flex items-center gap-4 rounded-3xl border border-brand-100 bg-white p-5">
            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full">
              <img src={LEADERSHIP[0].photo} alt={LEADERSHIP[0].name} className="h-full w-full object-cover object-top" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-ink-900">{LEADERSHIP[0].name}</h3>
              <p className="text-sm text-ink-600">{LEADERSHIP[0].title}</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={100} className="prose-gn lg:col-span-8">
          <p className="lead">Great North marks more than 36 years in business.</p>
          {PRESIDENT_MESSAGE.paragraphs.map((p) => (
            <p key={p.slice(0, 20)}>{p}</p>
          ))}
          <p>{PRESIDENT_MESSAGE.signoff}</p>
          <img src={IMAGES.signature} alt="Signature" className="my-4 h-16 w-auto" />
          <p className="!mt-0 font-semibold text-ink-900">{PRESIDENT_MESSAGE.name}<br /><span className="font-normal text-ink-600">{PRESIDENT_MESSAGE.title}</span></p>
        </Reveal>
      </div>
    </section>
    <CTA />
  </>
);
